import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Calculator, Handshake } from "lucide-react";
import { useLang } from "../../lib/lang";

/**
 * AudienceRouting — Feb 2026 redesign.
 *
 * Three clear conversion pathways sitting directly below the Hero, per the
 * approved homepage redesign:
 *   1. Design my home   → /consultation (free 30-min design session)
 *   2. Price a project  → /rfq          (CPQ wizard, 100-point scoring)
 *   3. Partner with us  → /trade-partners/apply  (contractor / designer / builder onboarding)
 *
 * This is a routing affordance, not a marketing claim block. Every card
 * points to an already-shipped page with its own copy and CRM plumbing.
 */
export default function AudienceRouting() {
  const { t } = useLang();

  const paths = [
    {
      icon: Compass,
      eyebrow: t("pathway.design.eyebrow"),
      title: t("pathway.design.title"),
      body: t("pathway.design.body"),
      cta: t("pathway.design.cta"),
      to: "/consultation",
      testid: "pathway-design",
    },
    {
      icon: Calculator,
      eyebrow: t("pathway.price.eyebrow"),
      title: t("pathway.price.title"),
      body: t("pathway.price.body"),
      cta: t("pathway.price.cta"),
      to: "/rfq",
      testid: "pathway-price",
    },
    {
      icon: Handshake,
      eyebrow: t("pathway.partner.eyebrow"),
      title: t("pathway.partner.title"),
      body: t("pathway.partner.body"),
      cta: t("pathway.partner.cta"),
      to: "/trade-partners/apply",
      testid: "pathway-partner",
    },
  ];

  return (
    <section
      data-testid="audience-routing"
      className="section-py bg-[#F5F2EB] border-y border-[#D7CFC4]"
    >
      <div className="container-x">
        <div className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="overline text-[#B08858]">{t("pathway.overline")}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05] text-[#0A0A0A]">
              {t("pathway.title_1")}<br />
              <em className="not-italic text-[#B08858]">{t("pathway.title_em")}</em>
            </h2>
          </div>
          <p className="max-w-md text-[#0A0A0A]/70 text-base md:text-lg leading-relaxed">
            {t("pathway.lede")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {paths.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={a.testid}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white border border-[#0A0A0A]/10 p-6 md:p-8 flex flex-col group hover:border-[#B08858] transition-colors duration-300"
                data-testid={a.testid}
              >
                <div className="w-12 h-12 border border-[#0A0A0A] group-hover:border-[#B08858] group-hover:text-[#B08858] flex items-center justify-center transition-colors">
                  <Icon size={20} />
                </div>
                <p className="text-[11px] tracking-[0.22em] uppercase text-[#B08858] mt-6">{a.eyebrow}</p>
                <h3 className="font-display text-2xl md:text-3xl mt-3 leading-tight text-[#0A0A0A]">
                  {a.title}
                </h3>
                <p className="mt-4 text-sm md:text-base text-[#0A0A0A]/70 leading-relaxed flex-1">
                  {a.body}
                </p>
                <Link
                  to={a.to}
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white px-5 py-3 text-xs tracking-[0.2em] uppercase hover:bg-[#B08858] transition-colors"
                  data-testid={`${a.testid}-cta`}
                >
                  {a.cta} <ArrowRight size={14} />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
